import React, { useState } from 'react'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import { Button, Card, Space, theme, Typography } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'

const { Text } = Typography

const Navigation = ({ currentIndex, total, onNavigate }) => {
    return (
        <Space>
            <Button
                shape='circle'
                icon={<LeftOutlined />}
                onClick={() => onNavigate(-1)}
                title='Previous question values'
                size='small'
                disabled={currentIndex === 0}
            />
            <Text type='secondary' className='text-sm'>
                {currentIndex + 1} / {total}
            </Text>
            <Button
                shape='circle'
                icon={<RightOutlined />}
                onClick={() => onNavigate(1)}
                aria-label='Next question values'
                size='small'
                disabled={currentIndex === total - 1}
            />
        </Space>
    )
}

const UnmatchedValuesList = ({ unmatchedRubricValues, initialIndex = 0 }) => {
    const [currentArrayIndex, setCurrentArrayIndex] = useState(initialIndex)

    const handleNavigate = direction => {
        setCurrentArrayIndex(prev => {
            const newIndex = prev + direction
            return Math.max(0, Math.min(newIndex, unmatchedRubricValues.length - 1))
        })
    }

    const currentValues = unmatchedRubricValues[currentArrayIndex] || []

    return (
        <Card
            title='Unmatched values'
            size='small'
            extra={
                <Navigation
                    currentIndex={currentArrayIndex}
                    total={unmatchedRubricValues.length}
                    onNavigate={handleNavigate}
                />
            }
        >
            <Droppable droppableId={`unmatched-list-${currentArrayIndex}`}>
                {provided => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                        {currentValues.map((item, index) => (
                            <UnmatchedItem key={item.id} item={item} index={index} />
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </Card>
    )
}

const UnmatchedItem = ({ item, index }) => {
    const { token } = theme.useToken()
    const [isSelectionMode, setIsSelectionMode] = useState(false)

    const handleDoubleClick = e => {
        e.preventDefault()
        setIsSelectionMode(true)
    }

    const handleBlur = () => setIsSelectionMode(false)

    return (
        <Draggable draggableId={String(item.id)} index={index} isDragDisabled={isSelectionMode}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onDoubleClick={handleDoubleClick}
                    onBlur={handleBlur}
                    style={{
                        ...provided.draggableProps.style,
                        padding: '4px',
                        margin: '8px 0',
                        backgroundColor: snapshot.isDragging ? '#e6f7ff' : 'white',
                        boxShadow: snapshot.isDragging ? token.boxShadowSecondary : 'none',
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        height: snapshot.isDragging ? '36px' : 'auto',
                        overflow: snapshot.isDragging ? 'hidden' : 'visible',
                        whiteSpace: snapshot.isDragging ? 'nowrap' : 'normal',
                        textOverflow: snapshot.isDragging ? 'ellipsis' : 'clip',
                        userSelect: isSelectionMode ? 'text' : 'none',
                        cursor: isSelectionMode ? 'text' : 'grab',
                        outline: isSelectionMode ? `2px solid ${token.colorPrimary}` : 'none',
                        zIndex: snapshot.isDragging ? 999999 : 'auto',
                    }}
                    tabIndex={0}
                >
                    {item.content}
                </div>
            )}
        </Draggable>
    )
}

export default UnmatchedValuesList
